import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { businessCardApi } from "./api";
import type { CardField, CardTemplate } from "./types";

const TEMPLATES_KEY = ["business-card-templates"] as const;

export function useCardTemplates() {
  return useQuery<CardTemplate[], Error>({
    queryKey: TEMPLATES_KEY,
    queryFn: () => businessCardApi.list(),
    staleTime: 1000 * 60,
  });
}

export function useCardTemplate(id: number | undefined) {
  return useQuery<CardTemplate, Error>({
    queryKey: [...TEMPLATES_KEY, id],
    queryFn: () => businessCardApi.get(id as number),
    enabled: id != null,
  });
}

export function useUploadCardTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, front, back }: { name: string; front: File; back?: File }) =>
      businessCardApi.upload(name, front, back),
    onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATES_KEY }),
  });
}

export function useSaveCardFields() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fields }: { id: number; fields: CardField[] }) =>
      businessCardApi.saveFields(id, fields),
    onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATES_KEY }),
  });
}

export function useActivateCardTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => businessCardApi.activate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: TEMPLATES_KEY }),
  });
}
